/**
 * Google Places API (New) — loader and adapter for PlacesAutocomplete.
 *
 * No React in here: this is the part that talks to Google and the part that
 * turns Google's answer into the address the forms print, kept apart so each
 * can be read on its own.
 *
 * Why Places (New): the classic `google.maps.places.Autocomplete` widget is not
 * offered to new customers since March 2025, and this project's key is refused
 * it (spec 016). The data API (`AutocompleteSuggestion`, `Place.fetchFields`)
 * leaves the input ours, so the field looks like its siblings and stays a plain
 * text box whenever Google is unavailable.
 */

export interface SelectedPlace {
  /** The whole thing, as Google formats it. */
  address: string;
  /** Street number + route, e.g. "4701 Rancho Del Norte". */
  street: string;
  city: string;
  /** Two-letter code ("TX") — the forms print the abbreviation. */
  state: string;
  zip: string;
  placeId: string | null;
  lat: number | null;
  lng: number | null;
}

// Minimal ambient shape of the bits of the Google Maps JS API we touch — the
// repo has no @types/google.maps and we only use a tiny slice.
interface GMapsAddressComponent {
  long_name: string;
  short_name: string;
  types: string[];
}

/**
 * Split Google's component list into the four fields a contract prints.
 *
 * The formatted address alone is not enough: the TREC/TXR forms have separate
 * boxes for street, city, state and ZIP, and splitting "4701 Rancho Del Norte,
 * McKinney, TX 75070" on commas breaks on the addresses that need it most —
 * unit numbers, and any city whose name contains one.
 *
 * `locality` is absent for some addresses (unincorporated areas, and a few
 * postal-town countries), so the usual fallbacks follow it — otherwise a valid
 * pick would silently leave City blank.
 *
 * The street keeps what a contract must not lose (Gaurav, spec 016 GATE 4):
 * the unit (`subpremise`, "suite 1000"), and the house number when Google's
 * details omit it but the suggestion the client picked shows it — Places
 * returns "1500 County Road 400, Princeton" as a suggestion and then details
 * with no `street_number`, which would print the road without the house.
 */
function splitComponents(
  parts: GMapsAddressComponent[] | undefined,
  picked = "",
): Pick<SelectedPlace, "street" | "city" | "state" | "zip"> {
  const find = (type: string, short = false) => {
    const hit = (parts ?? []).find((p) => p.types.includes(type));
    return hit ? (short ? hit.short_name : hit.long_name) : "";
  };
  const route = find("route");
  const streetNumber =
    find("street_number") || (route ? houseNumberIn(picked) : "");
  return {
    street: [streetNumber, route, find("subpremise")].filter(Boolean).join(" "),
    city:
      find("locality") ||
      find("postal_town") ||
      find("sublocality_level_1") ||
      find("administrative_area_level_2"),
    state: find("administrative_area_level_1", true),
    zip: find("postal_code"),
  };
}

/** "1500 County Road 400, Princeton, TX, USA" → "1500"; no leading number → "". */
function houseNumberIn(picked: string): string {
  return /^(\d+[A-Za-z]?)\s+\S/.exec(picked.trim())?.[1] ?? "";
}

/** An address component as Places (New) returns it. */
export interface PlacesAddressComponent {
  longText?: string | null;
  shortText?: string | null;
  types?: string[] | null;
}

/** The fields of a Places (New) `Place` we request and read. */
export interface PlacesPlace {
  id?: string | null;
  formattedAddress?: string | null;
  addressComponents?: PlacesAddressComponent[] | null;
  location?: { lat(): number; lng(): number } | null;
  fetchFields(req: { fields: string[] }): Promise<unknown>;
}

export interface PlacesPrediction {
  placeId: string;
  text: { text: string };
  toPlace(): PlacesPlace;
}

export interface PlacesLibrary {
  AutocompleteSessionToken: new () => object;
  AutocompleteSuggestion: {
    fetchAutocompleteSuggestions(req: {
      input: string;
      sessionToken: object;
      includedRegionCodes?: string[];
    }): Promise<{
      suggestions: { placePrediction?: PlacesPrediction | null }[];
    }>;
  };
}

/** Exactly the fields `toSelectedPlace` reads — asking for more is billable. */
export const PLACE_FIELDS = [
  "id",
  "formattedAddress",
  "addressComponents",
  "location",
];

/**
 * Places (New) names the component fields `longText`/`shortText`; the split
 * above reads the classic `long_name`/`short_name`. Translating here keeps the
 * split rules byte-for-byte what printed on the forms before (spec 016, FR-004).
 */
export function toLegacyComponents(
  parts: PlacesAddressComponent[] | null | undefined,
): GMapsAddressComponent[] {
  return (parts ?? []).map((p) => ({
    long_name: p.longText ?? "",
    short_name: p.shortText ?? "",
    types: p.types ?? [],
  }));
}

/** A fetched place → what the four address screens receive. */
export function toSelectedPlace(
  place: PlacesPlace,
  picked: string,
): SelectedPlace {
  return {
    address: place.formattedAddress || picked,
    ...splitComponents(toLegacyComponents(place.addressComponents), picked),
    placeId: place.id ?? null,
    lat: place.location?.lat() ?? null,
    lng: place.location?.lng() ?? null,
  };
}

// ---- loading ----------------------------------------------------------------

interface GoogleGlobal {
  google?: {
    maps?: { importLibrary?: (name: string) => Promise<unknown> };
  };
  gm_authFailure?: () => void;
}

/** The global Google calls once the Maps API is ready to `importLibrary`. */
const READY_CALLBACK = "__rpPlacesReady";

let placesPromise: Promise<PlacesLibrary | null> | null = null;
let authFailed = false;
const authListeners = new Set<() => void>();

/**
 * Google calls `window.gm_authFailure` when it refuses the key (wrong referrer,
 * API not allowed). That can happen after the library has loaded, so the
 * component subscribes rather than relying on the load result alone.
 */
export function onPlacesAuthFailure(listener: () => void): () => void {
  if (authFailed) listener();
  authListeners.add(listener);
  return () => authListeners.delete(listener);
}

/**
 * Load the Places library once per page. Resolves `null` — never rejects —
 * when there is no key, the script cannot load, `importLibrary` throws, or
 * Google has refused the key. Called on the first typed address, not on mount,
 * so a page that is merely viewed never contacts Google.
 */
export function loadPlaces(
  apiKey: string | undefined,
): Promise<PlacesLibrary | null> {
  if (!apiKey) return Promise.resolve(null);
  if (placesPromise) return placesPromise;

  placesPromise = new Promise<PlacesLibrary | null>((resolve) => {
    const w = window as unknown as GoogleGlobal;
    const previous = w.gm_authFailure;
    w.gm_authFailure = () => {
      authFailed = true;
      authListeners.forEach((l) => l());
      previous?.();
      resolve(null);
    };
    const importPlaces = () => {
      const importLibrary = w.google?.maps?.importLibrary;
      if (!importLibrary) return resolve(null);
      importLibrary("places").then(
        (lib) => resolve(authFailed ? null : (lib as PlacesLibrary)),
        () => resolve(null),
      );
    };
    if (w.google?.maps?.importLibrary) return importPlaces();

    // Under `loading=async`, `importLibrary` does NOT exist yet when the
    // script's onload fires — it appears a moment later, and Google then calls
    // the `callback=` function. Reading it at onload found nothing, and every
    // field on dev showed "suggestions are unavailable" (spec 016, GATE 5).
    (w as unknown as Record<string, unknown>)[READY_CALLBACK] = importPlaces;
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
      apiKey,
    )}&v=weekly&loading=async&libraries=places&callback=${READY_CALLBACK}`;
    script.async = true;
    script.onerror = () => resolve(null);
    document.head.appendChild(script);
  });
  return placesPromise;
}
